export const formatContent = (content, title = '', options = {}) => {
  const { isListicle = false } = options;

  // Remove title if it appears at the start
  if (title) {
    const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const titleRegex = new RegExp(`^\\s*(#\\s*)?${escapedTitle}\\s*\n`, 'i');
    content = content.replace(titleRegex, '');
  }

  let formattedContent = content;

  if (isListicle) {
    formattedContent = formattedContent
      .replace(/^\*\*(\d+)\.\s+(.+?)\*\*$/gm, '<h2>$1. $2</h2>')
      .replace(/^(\d+)\.\s+(.+?)$/gm, '<h2>$1. $2</h2>')
      .replace(/^\*\*([^*\n]+?)\*\*$/gm, (match, p1) => {
        if (p1.length < 100 && !/[.!?]/.test(p1)) {
          return `<h2>${p1}</h2>`;
        }
        return match;
      });
  }

  // Handle FAQ sections
  formattedContent = formattedContent
    .replace(/^(FAQ|Frequently Asked Questions|Common Questions)$/gim, '<h2>$1</h2>')
    .replace(/^Q:\s*(.+)$/gm, '<h3>$1</h3>');

  // Convert markdown headings to HTML
  formattedContent = formattedContent
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^#### (.*$)/gm, '<h4>$1</h4>');

  // Convert to DOM for processing
  const parser = new DOMParser();
  const doc = parser.parseFromString(formattedContent, 'text/html');

  // Style headings
  doc.querySelectorAll('h2').forEach((h2, index) => {
    if (isListicle && !h2.textContent.match(/^\d+\./)) {
      h2.textContent = `${index + 1}. ${h2.textContent}`;
    }
    h2.style.marginTop = '2em';
    h2.style.marginBottom = '1em';
    h2.style.fontWeight = '600';
    h2.style.fontSize = '1.5em';
    h2.id = `section-${index + 1}`;
  });

  // Process sections
  const sections = Array.from(doc.body.children);
  let currentSection = [];
  let standaloneFactsBox = null;

  sections.forEach((element, index) => {
    if (element.matches('h2, h3, h4')) {
      if (currentSection.length > 0) {
        processSection(currentSection, doc);
        currentSection = [];
      }
      currentSection.push(element);
      // Check for Basic Facts box before the next heading
      const nextElements = sections.slice(index + 1);
      const nextHeadingIndex = nextElements.findIndex(el => el.matches('h2, h3, h4'));
      if (nextHeadingIndex !== -1) {
        const elementsBeforeNextHeading = nextElements.slice(0, nextHeadingIndex);
        const factsBoxIndex = elementsBeforeNextHeading.findIndex(el => el.classList?.contains('article-basic-facts'));
        if (factsBoxIndex !== -1) {
          standaloneFactsBox = elementsBeforeNextHeading[factsBoxIndex];
          // Include only elements before Facts box in current section
          currentSection = currentSection.concat(elementsBeforeNextHeading.slice(0, factsBoxIndex));
          currentSection = currentSection.concat(elementsBeforeNextHeading.slice(factsBoxIndex + 1));
          currentSection = currentSection.concat(nextElements.slice(nextHeadingIndex));
          return;
        }
      }
    } else {
      currentSection.push(element);
    }
  });

  if (currentSection.length > 0) {
    processSection(currentSection, doc);
  }

  // Append Basic Facts box if found
  if (standaloneFactsBox) {
    doc.body.appendChild(standaloneFactsBox);
  }

  // Format lists
  doc.querySelectorAll('ul, ol').forEach(list => {
    list.style.marginBottom = '1em';
    list.style.marginLeft = '2em';
  });

  // Get the processed content
  formattedContent = doc.body.innerHTML;

  // Format remaining markdown
  formattedContent = formattedContent
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>');

  return formattedContent.trim();
};

function processSection(section, doc) {
  // Filter out special boxes and headings
  const specialBoxes = ['article-did-you-know', 'article-quick-tip', 'article-wow-factor', 'article-myth-buster', 'article-basic-facts'];
  const heading = section.find(el => el.matches('h2, h3, h4'));
  const contentElements = section.filter(el => !el.matches('h2, h3, h4'));

  // Process plain text into paragraphs
  const textNodes = contentElements.filter(el => !specialBoxes.some(cls => el.classList?.contains(cls)));
  const sentences = textNodes
    .map(el => el.textContent.trim())
    .filter(text => text)
    .join(' ')
    .split(/(?<=[.!?])\s+/);

  let paragraphs = [];
  let currentParagraph = [];

  sentences.forEach(sentence => {
    sentence = sentence.trim();
    if (!sentence) return;

    if (sentence.length > 150) {
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
      paragraphs.push(sentence);
    } else if (currentParagraph.join(' ').length + sentence.length > 200) {
      paragraphs.push(currentParagraph.join(' '));
      currentParagraph = [sentence];
    } else {
      currentParagraph.push(sentence);
      if (currentParagraph.length >= 2) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    }
  });

  if (currentParagraph.length > 0) {
    paragraphs.push(currentParagraph.join(' '));
  }

  // Insert elements in original order, interleaving paragraphs and special boxes
  let insertPoint = heading ? heading.nextSibling : doc.body.firstChild;
  let paragraphIndex = 0;

  contentElements.forEach(el => {
    if (specialBoxes.some(cls => el.classList?.contains(cls))) {
      // Insert special box at its original position, ensuring a paragraph comes first if available
      if (paragraphIndex < paragraphs.length) {
        const para = doc.createElement('p');
        para.textContent = paragraphs[paragraphIndex].trim();
        para.style.marginBottom = '1em';
        para.style.lineHeight = '1.7';
        doc.body.insertBefore(para, insertPoint);
        paragraphIndex++;
      }
      doc.body.insertBefore(el, insertPoint);
    } else if (el.textContent.trim() && paragraphIndex < paragraphs.length) {
      // Skip if already processed as a paragraph
      const para = doc.createElement('p');
      para.textContent = paragraphs[paragraphIndex].trim();
      para.style.marginBottom = '1em';
      para.style.lineHeight = '1.7';
      doc.body.insertBefore(para, insertPoint);
      paragraphIndex++;
    }
  });

  // Insert remaining paragraphs
  while (paragraphIndex < paragraphs.length) {
    const para = doc.createElement('p');
    para.textContent = paragraphs[paragraphIndex].trim();
    para.style.marginBottom = '1em';
    para.style.lineHeight = '1.7';
    doc.body.insertBefore(para, insertPoint);
    paragraphIndex++;
  }
}